import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import "./NavBar.css";

const NavBar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const isHome = location.pathname === "/";
  const isAbout = location.pathname === "/about";
  const isHeroPage = isHome || isAbout;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isSolid = !isHeroPage || scrolled || menuOpen;

  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
  };

  return (
    <nav
      className={`navbar ${isSolid ? "navbar-solid" : "navbar-transparent"}`}
    >
      <div className="navbar-inner">
        {/* Left — brand */}
        <div className="navbar-brand">
          <Link to="/" className="brand-link">
            Short Cuts NYC
          </Link>
        </div>

        {/* Center — nav links (desktop) */}
        <ul className="navbar-links">
          <li className={location.pathname === "/" ? "active" : ""}>
            <Link to="/" className="nav-link">
              Home
            </Link>
          </li>
          <li className={location.pathname === "/about" ? "active" : ""}>
            <Link to="/about" className="nav-link">
              About
            </Link>
          </li>
          <li className={location.pathname === "/submit" ? "active" : ""}>
            <Link to="/submit" className="nav-link nav-link-primary">
              Submit Film
            </Link>
          </li>
          <li className={location.pathname === "/tracker" ? "active" : ""}>
            <Link to="/tracker" className="nav-link">
              Frames Tracker
            </Link>
          </li>
          {isAdmin && (
            <li
              className={location.pathname === "/submissions" ? "active" : ""}
            >
              <Link to="/submissions" className="nav-link nav-link-admin">
                Admin
              </Link>
            </li>
          )}
          {isAuthenticated && (
            <li>
              <a href="#logout" onClick={handleLogout} className="nav-link">
                Logout
              </a>
            </li>
          )}
        </ul>

        {/* Right — social icons (desktop) */}
        <div className="navbar-socials">
          <a
            href="https://www.meetup.com/short-cuts/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Meetup"
          >
            <img src="/meetup.svg" alt="Meetup" className="social-icon-img" />
          </a>
          <a
            href="https://discord.gg/b9KRsFd2bQ"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Discord"
          >
            <img src="/discord.svg" alt="Discord" className="social-icon-img" />
          </a>
          <a
            href="https://www.instagram.com/shortcutsny/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Instagram"
          >
            <img
              src="/instagram.svg"
              alt="Instagram"
              className="social-icon-img"
            />
          </a>
          <a
            href="https://venmo.com/shortcutsnyc"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Donate via Venmo"
          >
            <img src="/venmo.svg" alt="Venmo" className="social-icon-img" />
          </a>
        </div>

        {/* Hamburger button (mobile only) */}
        <button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? (
            <X size={24} color="#d1c5b2" />
          ) : (
            <Menu size={24} color="#d1c5b2" />
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu-open" : ""}`}>
        <Link to="/" className="mobile-link" onClick={() => setMenuOpen(false)}>
          Home
        </Link>
        <Link
          to="/about"
          className="mobile-link"
          onClick={() => setMenuOpen(false)}
        >
          About
        </Link>
        <Link
          to="/submit"
          className="mobile-link mobile-link-primary"
          onClick={() => setMenuOpen(false)}
        >
          Submit Film
        </Link>
        <Link
          to="/tracker"
          className="mobile-link"
          onClick={() => setMenuOpen(false)}
        >
          Frames Tracker
        </Link>
        {isAdmin && (
          <Link
            to="/submissions"
            className="mobile-link"
            onClick={() => setMenuOpen(false)}
          >
            Admin
          </Link>
        )}
        {isAuthenticated && (
          <a href="#logout" onClick={handleLogout} className="mobile-link">
            Logout
          </a>
        )}
        <div className="mobile-socials">
          <a
            href="https://www.meetup.com/short-cuts/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Meetup"
          >
            <img src="/meetup.svg" alt="Meetup" className="social-icon-img" />
          </a>
          <a
            href="https://discord.gg/b9KRsFd2bQ"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Discord"
          >
            <img src="/discord.svg" alt="Discord" className="social-icon-img" />
          </a>
          <a
            href="https://www.instagram.com/shortcutsny/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Instagram"
          >
            <img
              src="/instagram.svg"
              alt="Instagram"
              className="social-icon-img"
            />
          </a>
          <a
            href="https://venmo.com/shortcutsnyc"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon-link"
            aria-label="Venmo"
          >
            <img src="/venmo.svg" alt="Venmo" className="social-icon-img" />
          </a>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
